import * as React from "react"

import { useTranslations } from "@/i18n"
import { useWidgetMenu } from "@ibada/dashboard-engine"

import { WidgetBoundary } from "@/features/finance/overview/components/widget-boundary"
import { formatMoneySafe, formatPercent } from "@/features/finance/overview/lib/overview-format"
import { OverviewRoutes, useOverviewNav } from "@/features/finance/overview/lib/overview-nav"
import {
  PerformanceTableSkeleton,
  toneForHealth,
  useBudgetPerformance,
} from "@/features/finance/overview/widgets/budget-performance-shared"
import type { BudgetPerformanceLine } from "@/features/budgeting/model/budget-performance-types"

const MAX_ROWS = 8

const TONE_TEXT: Record<string, string> = {
  success: "text-emerald-600 dark:text-emerald-400",
  warning: "text-amber-600 dark:text-amber-400",
  destructive: "text-destructive",
  muted: "text-muted-foreground",
}

/**
 * Budget Line Performance — the ACCOUNT x FUND matrix, one row per budget line.
 *
 * Both dimensions are shown because they answer different questions and neither substitutes for the other:
 * the account says where the money came from (or went), the fund says which pool it belongs to.
 */
export function BudgetLinePerformanceWidget() {
  const t = useTranslations("finance.overview")
  const go = useOverviewNav()
  const query = useBudgetPerformance()
  const currency = query.data?.currency ?? ""

  const rows = React.useMemo<BudgetPerformanceLine[] | undefined>(() => {
    if (!query.data) return undefined
    return [...query.data.lines]
      .sort((a, b) => (b.utilizationPct ?? 0) - (a.utilizationPct ?? 0))
      .slice(0, MAX_ROWS)
  }, [query.data])

  useWidgetMenu({
    refresh: () => void query.refetch(),
    viewDetails: go.canNavigateTo(OverviewRoutes.budgets) ? () => go(OverviewRoutes.budgets) : undefined,
  })

  return (
    <WidgetBoundary
      isLoading={query.isLoading}
      isError={query.isError}
      isFetching={query.isFetching}
      data={rows}
      isEmpty={(list) => list.length === 0}
      skeleton={<PerformanceTableSkeleton />}
      errorTitle={t("widgets.budgetLinePerformance.error")}
      onRetry={() => void query.refetch()}
      retryLabel={t("widgets.common.retry")}
      emptyTitle={t("widgets.budgetLinePerformance.empty.title")}
    >
      {(list) => (
        // Wide content scrolls inside its own container so the dashboard column never scrolls sideways.
        <div className="overflow-x-auto">
          <table className="w-full min-w-[34rem] text-xs">
            <thead>
              <tr className="text-left text-muted-foreground">
                <th className="pb-2 pr-3 font-medium">{t("widgets.budgetLinePerformance.columns.account")}</th>
                <th className="pb-2 pr-3 font-medium">{t("widgets.budgetLinePerformance.columns.fund")}</th>
                <th className="pb-2 pr-3 text-right font-medium">{t("widgets.budgetLinePerformance.columns.planned")}</th>
                <th className="pb-2 pr-3 text-right font-medium">{t("widgets.budgetLinePerformance.columns.actual")}</th>
                <th className="pb-2 pr-3 text-right font-medium">{t("widgets.budgetLinePerformance.columns.variance")}</th>
                <th className="pb-2 text-right font-medium">{t("widgets.budgetLinePerformance.columns.utilization")}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/60">
              {list.map((line) => (
                <tr key={line.budgetLineId}>
                  <td className="max-w-[10rem] truncate py-1.5 pr-3 text-foreground">
                    {line.accountName ?? line.lineTitle}
                  </td>
                  <td className="max-w-[9rem] truncate py-1.5 pr-3 text-muted-foreground">
                    {line.fundName ?? "—"}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
                    {formatMoneySafe(line.planned, currency)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-foreground">
                    {formatMoneySafe(line.actual, currency)}
                  </td>
                  <td className="py-1.5 pr-3 text-right tabular-nums text-muted-foreground">
                    {formatMoneySafe(line.variance, currency)}
                  </td>
                  <td
                    className={`py-1.5 text-right tabular-nums font-medium ${TONE_TEXT[toneForHealth(line.healthStatus)] ?? ""}`}
                  >
                    {line.utilizationPct === null ? "—" : formatPercent(line.utilizationPct)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </WidgetBoundary>
  )
}
